import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useVideoManagerStore = defineStore('videoManager', () => {
  const currentlyPlayingVideo = ref<HTMLVideoElement | null>(null)
  const headerVideo = ref<HTMLVideoElement | null>(null)
  const videoEntries = new Map<HTMLVideoElement, number>()

  function getDistanceFromCenter(element: HTMLElement): number {
    const rect = element.getBoundingClientRect()
    const elementCenter = rect.top + rect.height / 2
    const viewportCenter = window.innerHeight / 2
    return Math.abs(elementCenter - viewportCenter)
  }

  const pauseHeaderVideo = () => {
    if (headerVideo.value) {
      headerVideo.value.pause()
      headerVideo.value.dispatchEvent(new Event('videoStateUpdate'))
    }
  }

  const updateVideoDistance = (video: HTMLVideoElement, isIntersecting: boolean) => {
    if (video === headerVideo.value) return

    if (isIntersecting) {
      videoEntries.set(video, getDistanceFromCenter(video))
    } else {
      videoEntries.delete(video)
    }

    let closestVideo: HTMLVideoElement | null = null
    let minDistance = Infinity

    videoEntries.forEach((distance, vid) => {
      if (distance < minDistance) {
        minDistance = distance
        closestVideo = vid
      }
    })

    videoEntries.forEach((_, vid) => {
      if (vid === closestVideo) {
        vid.muted = true
        vid.play().then(() => {
          pauseHeaderVideo()
          currentlyPlayingVideo.value = vid
          vid.dispatchEvent(new Event('videoStateUpdate'))
        }).catch((error) => {
          console.warn('Autoplay failed:', error)
        })
      } else if (vid !== headerVideo.value) {
        vid.pause()
        vid.dispatchEvent(new Event('videoStateUpdate'))
      }
    })
  }

  const handleUnmute = (video: HTMLVideoElement) => {
    if (video !== headerVideo.value) {
      pauseHeaderVideo()
    }
  }

  const setCurrentlyPlaying = (video: HTMLVideoElement | null) => {
    if (video && video !== headerVideo.value) {
      pauseHeaderVideo()
    }
    if (currentlyPlayingVideo.value && video !== currentlyPlayingVideo.value) {
      currentlyPlayingVideo.value.pause()
      currentlyPlayingVideo.value.dispatchEvent(new Event('videoStateUpdate'))
    }
    currentlyPlayingVideo.value = video
  }

  const setHeaderVideo = (video: HTMLVideoElement) => {
    headerVideo.value = video
  }

  return {
    currentlyPlayingVideo,
    setCurrentlyPlaying,
    updateVideoDistance,
    setHeaderVideo,
    handleUnmute,
  }
})
